import { useState, type ReactNode } from 'react';
import {
  Alert,
  Badge,
  Button,
  Fact,
  NotProvided,
  PhotoViewer,
  SideDrawer,
  StepPath,
  SummaryCell,
  SummaryStrip,
  ThumbStrip,
  linkTextClasses,
  type PathStep,
} from '@evogard/ui';
import type {
  Claim,
  ClaimDocument,
  ClaimSummary,
  RepairSummary,
} from '@evogard/contract';
import {
  REPAIR_PHASES,
  catalogValueText,
  formatInstant,
  formatPhone,
  type RepairStage,
} from '@evogard/domain';
import { CLAIM_TYPE_OPTIONS, labelOf } from './labels.js';
import { useNoticePhotos } from './noticeFiles.js';
import type { useRepairSummary } from './useDesk.js';

type SummaryQuery = ReturnType<typeof useRepairSummary>;

/**
 * O bloco do alto da mesa do Reparo, e a gaveta dele (07/10/2026) — a única
 * parte da mesa que é diferente da Análise. Onde a Análise mostra o ocorrido,
 * o Reparo mostra a oficina, o marco da vez e as peças; "Ver reparo" abre a
 * gaveta com a oficina, os marcos do conserto, o orçamento, as peças, as fotos
 * de antes e depois e, no pé, o ocorrido numa linha.
 *
 * Nada aqui se digita: é o que os formulários das etapas gravaram
 * (`GET /claims/:id/repair-summary`). O que ainda não foi preenchido aparece
 * como espera, com a etapa que o preenche — a tela não inventa data.
 */
export function RepairSummaryStrip({
  summary,
  phase,
  open,
  onToggle,
}: {
  summary: SummaryQuery;
  phase: string | null;
  open: boolean;
  onToggle: () => void;
}) {
  const action = {
    label: open ? 'Fechar reparo' : 'Ver reparo',
    onClick: onToggle,
    expanded: open,
  };
  if (summary.isError) {
    return (
      <SummaryStrip label="Reparo do veículo" action={action}>
        <SummaryCell
          value="Não foi possível ler o reparo."
          detail={summary.error.message}
        />
      </SummaryStrip>
    );
  }
  const s = summary.data;
  if (!s) {
    return (
      <SummaryStrip label="Reparo do veículo" action={action}>
        <SummaryCell value="Carregando o reparo…" />
      </SummaryStrip>
    );
  }
  const workshop = s.workshop;
  return (
    <SummaryStrip label="Reparo do veículo" action={action}>
      <SummaryCell
        value={workshop?.name ?? 'Sem oficina'}
        detail={
          workshop
            ? workshop.city || formatPhone(workshop.phone) || workshop.email || 'Oficina'
            : 'escolhida na Orçamentação'
        }
        title={workshop?.name}
      />
      {beforeApproval(phase) && <QuoteDueCell dueAt={s.quoteDueAt} />}
      <KeyCell summary={s} />
      <PartsCell summary={s} />
    </SummaryStrip>
  );
}

/** O prazo de orçamentação, em destaque enquanto o orçamento não foi aprovado (Q1). */
function QuoteDueCell({ dueAt }: { dueAt: string | null }) {
  if (!dueAt) {
    return <SummaryCell value="Orçamento sem prazo" detail="definido na Orçamentação" />;
  }
  const day = localDay(dueAt) ?? dueAt.slice(0, 10);
  const hour = new Date(dueAt).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <SummaryCell
      value={<span className="tabular-nums">Orçamento até {ddmm(day)}</span>}
      detail={
        <span className="inline-flex items-center gap-1.5">
          <span className="tabular-nums">{hour}</span>
          <DueBadge on={day} />
        </span>
      }
      title={`Prazo de orçamentação: ${formatInstant(dueAt)}`}
      noShrink
    />
  );
}

/**
 * O marco da vez — muda conforme o reparo anda: a parada agendada, o veículo
 * na oficina, a entrega prevista, ou a perda total que levou à Indenização.
 */
function KeyCell({ summary: s }: { summary: RepairSummary }) {
  const d = s.dates;
  if (s.quote?.vehicleSituation === 'perda_total') {
    return <SummaryCell value="Perda total" detail="seguiu para a Indenização" />;
  }
  if (d.quoteApprovedOn && d.deliveryForecastOn && !d.deliveredAt) {
    return (
      <SummaryCell
        value={
          <span className="tabular-nums">
            Entrega prevista {ddmm(d.deliveryForecastOn)}
          </span>
        }
        detail={
          d.vehicleReadyOn ? 'veículo pronto' : <DueBadge on={d.deliveryForecastOn} />
        }
        noShrink
      />
    );
  }
  if (d.deliveredAt) {
    return (
      <SummaryCell
        value={
          <span className="tabular-nums">Entregue {formatInstant(d.deliveredAt)}</span>
        }
        detail="ao cliente"
        noShrink
      />
    );
  }
  /* A primeira chegada à oficina: a para orçamento, ou — no sinistro de antes
     dela existir (27/10/2026) — a para o reparo. */
  const inShopSince = d.vehicleStoppedForQuoteOn ?? d.vehicleStoppedOn;
  if (inShopSince) {
    return (
      <SummaryCell
        value={<span className="tabular-nums">Na oficina desde {ddmm(inShopSince)}</span>}
        detail={d.quoteSentOn ? 'orçamento enviado' : 'orçamento em elaboração'}
        noShrink
      />
    );
  }
  if (s.scheduling.scheduledFor) {
    return (
      <SummaryCell
        value={
          <span className="tabular-nums">
            Parada agendada {ddmm(s.scheduling.scheduledFor)}
          </span>
        }
        detail={<DueBadge on={s.scheduling.scheduledFor} />}
        noShrink
      />
    );
  }
  return (
    <SummaryCell
      value="Sem agendamento"
      detail={
        s.scheduling.status
          ? (optionLabel('aguardando_parada', 'schedulingStatus', s.scheduling.status) ??
            '')
          : 'parada a combinar'
      }
    />
  );
}

/** As peças: depois da aprovação, a previsão com o selo de prazo, ou a chegada. */
function PartsCell({ summary: s }: { summary: RepairSummary }) {
  const p = s.parts;
  if (!p) return <SummaryCell value="Peças após aprovação" detail="do orçamento" />;
  if (p.arrivedOn) {
    return (
      <SummaryCell
        value={<span className="tabular-nums">Peças chegaram {ddmm(p.arrivedOn)}</span>}
        detail={optionLabel('compra_de_pecas', 'supplyKind', p.supplyKind) ?? undefined}
        noShrink
      />
    );
  }
  return (
    <SummaryCell
      value={
        p.forecastOn ? (
          <span className="tabular-nums">Peças previstas {ddmm(p.forecastOn)}</span>
        ) : (
          'Peças pedidas'
        )
      }
      detail={p.forecastOn ? <DueBadge on={p.forecastOn} /> : 'sem previsão'}
      noShrink
    />
  );
}

/**
 * A gaveta do reparo: a oficina e o contato, os marcos com as datas, o
 * orçamento aprovado, as peças, as fotos de antes e depois e, no pé, o
 * ocorrido numa linha — que abre a gaveta do ocorrido, com o caminho de volta.
 */
export function RepairDrawer({
  claim,
  root,
  summary,
  onOpenOccurrence,
  onClose,
}: {
  claim: ClaimSummary;
  root: Claim | undefined;
  summary: SummaryQuery;
  onOpenOccurrence: () => void;
  onClose: () => void;
}) {
  const s = summary.data;
  return (
    <SideDrawer
      eyebrow={`Reparo do aviso #${claim.id}`}
      title="O reparo do veículo"
      closeLabel="Fechar o reparo"
      onClose={onClose}
    >
      {summary.isError && <Alert tone="erro">{summary.error.message}</Alert>}
      {!s && !summary.isError && (
        <p className="text-sm text-texto-suave">Carregando o reparo…</p>
      )}
      {s && (
        <>
          <Section title="Oficina" aside="indicada na Orçamentação">
            {s.workshop ? (
              <div className="flex flex-col gap-1 rounded-borda border border-borda px-3 py-2.5">
                <span className="font-semibold">{s.workshop.name}</span>
                {(s.workshop.address || s.workshop.city) && (
                  <span className="text-sm text-texto-suave">
                    {[s.workshop.address, s.workshop.city].filter(Boolean).join(' · ')}
                  </span>
                )}
                {s.workshop.phone && (
                  <a
                    href={`tel:+55${s.workshop.phone}`}
                    className={linkTextClasses('self-start text-sm tabular-nums')}
                  >
                    {formatPhone(s.workshop.phone)}
                  </a>
                )}
                {s.workshop.email && (
                  <span className="text-sm text-texto-suave">{s.workshop.email}</span>
                )}
                {!s.workshop.phone && !s.workshop.email && (
                  <span className="text-sm text-texto-suave">
                    O cadastro da oficina não tem telefone nem e-mail.
                  </span>
                )}
              </div>
            ) : (
              <Wait>Nenhuma oficina indicada. Ela é escolhida na Orçamentação.</Wait>
            )}
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Fact label="Prazo de orçamentação">
                {s.quoteDueAt ? (
                  <span className="tabular-nums">{formatInstant(s.quoteDueAt)}</span>
                ) : (
                  <NotProvided />
                )}
              </Fact>
            </dl>
          </Section>

          {/* `type` (associado | terceiro), e não `claimType`, que é o evento —
              com o evento, o Pagamento da PI nunca aparecia (até 27/10/2026). */}
          <Milestones summary={s} claimType={claim.type} />

          <Section title="Orçamento">
            {s.quote?.approvalResult ? (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Fact label="Nº no Cilia">
                  {s.quote.ciliaNumber ? (
                    <span className="font-mono">{s.quote.ciliaNumber}</span>
                  ) : (
                    <NotProvided />
                  )}
                </Fact>
                <Fact label="Tipo">
                  {optionLabel('orcamentacao_oficina', 'quoteKind', s.quote.kind) ?? (
                    <NotProvided />
                  )}
                </Fact>
                <Fact label="Total geral">
                  {s.quote.grossTotal ? (
                    <span className="tabular-nums">
                      {valueText(
                        'orcamentacao_oficina',
                        'grossTotal',
                        s.quote.grossTotal,
                      )}
                    </span>
                  ) : (
                    <NotProvided />
                  )}
                </Fact>
                <Fact label="Supervisão">
                  <Badge
                    tone={s.quote.approvalResult === 'aprovado' ? 'sucesso' : 'erro'}
                  >
                    {optionLabel(
                      'aprovacao_supervisor',
                      'supervisionResult',
                      s.quote.approvalResult,
                    )}
                  </Badge>
                </Fact>
                <Fact label="Situação do veículo">
                  {optionLabel(
                    'aprovacao_supervisor',
                    'vehicleSituation',
                    s.quote.vehicleSituation,
                  ) ?? <NotProvided />}
                </Fact>
                <Fact label="Pagamento PI">
                  {optionLabel(
                    'aprovacao_supervisor',
                    'participationPayment',
                    s.quote.participationPayment,
                  ) ?? <NotProvided />}
                </Fact>
              </dl>
            ) : (
              <Wait>
                {s.dates.vehicleStoppedForQuoteOn ||
                s.dates.vehicleStoppedOn ||
                s.dates.quoteSentOn
                  ? 'Em elaboração pela oficina. Os valores aparecem aqui depois da Aprovação Supervisor.'
                  : 'Começa quando o veículo parar na oficina.'}
              </Wait>
            )}
          </Section>

          <Section title="Peças">
            {s.parts ? (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Fact label="Fornecimento">
                  {optionLabel('compra_de_pecas', 'supplyKind', s.parts.supplyKind) ?? (
                    <NotProvided />
                  )}
                </Fact>
                <Fact label="Pedido em">
                  {s.parts.requestedOn ? ddmmyyyy(s.parts.requestedOn) : <NotProvided />}
                </Fact>
                <Fact label="Previsão">
                  {s.parts.forecastOn ? ddmmyyyy(s.parts.forecastOn) : <NotProvided />}
                </Fact>
                <Fact label="Chegada">
                  {s.parts.arrivedOn ? (
                    ddmmyyyy(s.parts.arrivedOn)
                  ) : (
                    <Badge tone="informacao">aguardando</Badge>
                  )}
                </Fact>
              </dl>
            ) : (
              <Wait>A compra das peças começa depois do orçamento aprovado.</Wait>
            )}
          </Section>

          <Photos
            claimId={claim.id}
            quote={s.photos.quote}
            inspection={s.photos.inspection}
          />
        </>
      )}

      {root && (
        <div className="flex items-center justify-between gap-3 border-t border-borda pt-3 text-sm text-texto-suave">
          <span>
            <span className="font-semibold text-texto">
              {labelOf(CLAIM_TYPE_OPTIONS, root.claimType)}
            </span>{' '}
            em <span className="tabular-nums">{formatInstant(root.occurredAt)}</span>
          </span>
          <Button size="sm" variant="discreto" onClick={onOpenOccurrence}>
            Ver o ocorrido
          </Button>
        </div>
      )}
    </SideDrawer>
  );
}

/**
 * Os marcos do conserto, na ordem, com a data real, a prevista ou "aguardando".
 *
 * A ordem é a do acompanhamento no Pipefy (27/10/2026): do aviso à vistoria,
 * com as DUAS paradas — a para orçamento, na Aguardando Parada, e a para o
 * reparo, no Reparo - Oficina —, cada uma com a previsão da sua etapa. Antes a
 * "Parada na oficina" era a primeira da linha e juntava a previsão de uma com
 * a data real da outra.
 */
function Milestones({
  summary: s,
  claimType,
}: {
  summary: RepairSummary;
  /** O pagamento da PI é marco só do associado (R7). */
  claimType: string;
}) {
  const d = s.dates;
  const raw: { title: string; real: string | null; plan?: string | null }[] = [
    { title: 'Aviso', real: d.notifiedOn },
    { title: 'Entrada no Reparo', real: d.repairEnteredOn },
    {
      title: 'Parada para orçamento',
      real: d.vehicleStoppedForQuoteOn,
      plan: s.scheduling.scheduledFor,
    },
    { title: 'Envio do orçamento', real: d.quoteSentOn },
    { title: 'Aprovação do orçamento', real: d.quoteApprovedOn },
    ...(claimType === 'associado'
      ? [{ title: 'Pagamento da PI', real: d.participationPaidOn }]
      : []),
    { title: 'Compra das peças', real: s.parts?.requestedOn ?? null },
    {
      title: 'Parada na oficina',
      real: d.vehicleStoppedOn,
      plan: s.scheduling.repairScheduledFor,
    },
    { title: 'Chegada das peças', real: d.partsArrivedOn, plan: s.parts?.forecastOn },
    { title: 'Início do reparo', real: d.repairStartedOn },
    { title: 'Veículo pronto', real: d.vehicleReadyOn, plan: d.deliveryForecastOn },
    { title: 'Vistoria de entrega', real: d.inspectedOn },
  ];
  /*
   * "Agora" é o primeiro marco sem data DEPOIS do último que tem. O que ficou
   * para trás sem data — o sinistro de antes da parada para orçamento existir,
   * ou o orçamento feito com o carro com o cliente — diz que não foi
   * registrado, em vez de prender a linha nele.
   */
  const lastDone = raw.reduce((at, m, i) => (m.real ? i : at), -1);
  const nowIndex = raw.findIndex((m, i) => i > lastDone && !m.real);
  const steps: PathStep[] = raw.map((m, i) => {
    const state = m.real ? 'done' : i === nowIndex ? 'now' : 'next';
    return {
      key: m.title,
      title: m.title,
      state,
      detail: m.real ? (
        <span className="tabular-nums">{ddmmyyyy(m.real)}</span>
      ) : i < lastDone ? (
        'não registrada'
      ) : m.plan ? (
        <span className="inline-flex items-center gap-1.5 tabular-nums">
          prev. {ddmmyyyy(m.plan)} <DueBadge on={m.plan} />
        </span>
      ) : state === 'now' ? (
        'aguardando'
      ) : undefined,
    };
  });
  const done = raw.filter((m) => m.real).length;
  return (
    <Section title="Marcos do reparo" aside={`${done} de ${raw.length}`}>
      <StepPath label="Os marcos do reparo" steps={steps} />
    </Section>
  );
}

/** As fotos de antes (no orçamento) e de depois (na vistoria de entrega). */
function Photos({
  claimId,
  quote,
  inspection,
}: {
  claimId: number;
  quote: readonly ClaimDocument[];
  inspection: readonly ClaimDocument[];
}) {
  const before = useNoticePhotos(claimId, quote);
  const after = useNoticePhotos(claimId, inspection);
  const [open, setOpen] = useState<{ set: 'antes' | 'depois'; index: number } | null>(
    null,
  );
  const photos = open?.set === 'antes' ? before.data : open ? after.data : undefined;
  return (
    <Section title="Fotos: antes e depois">
      {open && photos && photos.length > 0 && (
        <PhotoViewer
          photos={photos}
          index={open.index}
          onIndexChange={(index) => setOpen({ set: open.set, index })}
        />
      )}
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-texto-suave">No orçamento</span>
        <ThumbStrip
          photos={before.data ?? []}
          max={6}
          onOpen={(index) => setOpen({ set: 'antes', index })}
          empty="Chegam com o orçamento da oficina."
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-texto-suave">
          Na vistoria de entrega
        </span>
        <ThumbStrip
          photos={after.data ?? []}
          max={6}
          onOpen={(index) => setOpen({ set: 'depois', index })}
          empty="Chegam na vistoria de entrega, para comparar com as do orçamento."
        />
      </div>
    </Section>
  );
}

export function Section({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: string;
  children: ReactNode;
}) {
  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <h4 className="flex items-baseline justify-between gap-2 text-xs font-semibold tracking-wide text-texto-tenue uppercase">
        {title}
        {aside && (
          <span className="text-xs font-semibold tracking-normal normal-case">
            {aside}
          </span>
        )}
      </h4>
      {children}
    </section>
  );
}

export function Wait({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-borda border border-dashed border-borda px-3 py-2.5 text-sm text-texto-suave">
      {children}
    </p>
  );
}

/** O selo de prazo de uma data: no prazo, vence em breve, ou atrasada. */
function DueBadge({ on }: { on: string }) {
  const days = daysTo(on);
  if (days === null) return null;
  if (days < 0) {
    return <Badge tone="erro">atrasada {-days === 1 ? '1 dia' : `${-days} dias`}</Badge>;
  }
  if (days <= 2) {
    return (
      <Badge tone="alerta">
        {days === 0 ? 'vence hoje' : `vence em ${days === 1 ? '1 dia' : `${days} dias`}`}
      </Badge>
    );
  }
  return <Badge tone="sucesso">no prazo</Badge>;
}

/** Antes da aprovação do orçamento — enquanto o prazo de orçamentação conta. */
function beforeApproval(phase: string | null): boolean {
  /* A Orçamentação é a entrada desde 03/11/2026: a Supervisão saiu, e a
     Aguardando Parada passou para depois da aprovação. */
  return phase === 'orcamentacao_oficina';
}

/** O rótulo de uma opção gravada, pelo campo do catálogo do Reparo. */
function optionLabel(
  phase: RepairStage,
  key: string,
  code: string | null,
): string | null {
  if (!code) return null;
  const field = REPAIR_PHASES[phase].fields.find((f) => f.key === key);
  return field?.options?.find((o) => o.value === code)?.label ?? code;
}

/** O valor gravado como se lê — o dinheiro em reais. */
function valueText(phase: RepairStage, key: string, value: string): string {
  const field = REPAIR_PHASES[phase].fields.find((f) => f.key === key);
  return field ? catalogValueText(field, value) : value;
}

/** Quantos dias faltam até a data (negativo: passou). */
function daysTo(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const target = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86_400_000);
}

/** O dia de um instante, no fuso de quem vê. */
function localDay(instant: string): string | null {
  const d = new Date(instant);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const ddmmyyyy = (iso: string) =>
  `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
